/**
 * PatternLibrary — modal for browsing, inserting, and saving pattern presets.
 *
 * Lists built-in presets (shipped) and user presets (from IndexedDB).
 * Insert "applies" a preset to the currently visible pattern starting at
 * the cursor row. "Save current pattern as preset…" captures the visible
 * pattern's rows (filtered to the user-picked channels) as a new user
 * preset.
 */

import { useEffect, useMemo, useState } from 'react';
import type { DemoRow } from '../audio/demo-song';
import type { ChannelName } from '../lib/keyboard';
import {
  BUILTIN_PRESETS,
  PRESET_CATEGORIES,
  ROOT_LETTERS,
  isLoopable,
  rootDelta,
  type PatternPreset,
  type PresetCategory,
  type RootLetter,
} from '../lib/pattern-presets';
import { deletePreset, listPresets, savePreset } from '../lib/persistence';

interface Props {
  open: boolean;
  onClose: () => void;
  /** Rows of the currently selected pattern, for "save as preset". */
  currentPatternRows: DemoRow[];
  rowsPerPattern: number;
  cursorRow: number;
  onInsert: (
    preset: PatternPreset,
    atRow: number,
    transposeSemitones: number,
    loop: boolean,
  ) => void;
}

type Mode = 'browse' | 'save';

const CATEGORY_LABEL: Record<PresetCategory, string> = {
  full: '🌟 Full mix',
  drums: '🥁 Drums',
  bass: '🎸 Bass',
  melody: '🎵 Melody',
  harmony: '🎶 Harmony',
  fill: '⚡ Fill',
};

interface ChannelBadgeStyle {
  label: string;
  cls: string;
}

const CHANNEL_BADGE: Record<ChannelName, ChannelBadgeStyle> = {
  pulse1: { label: 'P1', cls: 'bg-nes-cyan/30 text-nes-cyan' },
  pulse2: { label: 'P2', cls: 'bg-nes-cyan/15 text-nes-cyan/80 border border-nes-cyan/40' },
  triangle: { label: 'TRI', cls: 'bg-nes-white/15 text-nes-white' },
  noise: { label: 'NSE', cls: 'bg-nes-red/25 text-nes-red' },
  dpcm: { label: 'DPCM', cls: 'bg-amber-300/20 text-amber-200 border border-amber-300/40' },
};

function ChannelBadge({ channel }: { channel: ChannelName }) {
  const s = CHANNEL_BADGE[channel];
  return (
    <span
      className={`rounded px-1.5 py-[1px] text-[10px] font-bold tracking-wider ${s.cls}`}
      title={`Touches the ${channel} channel`}
    >
      {s.label}
    </span>
  );
}

function ChannelBadges({ channels }: { channels: ChannelName[] }) {
  // Display in canonical order so the visual rhythm matches the pattern grid.
  const order: ChannelName[] = ['pulse1', 'pulse2', 'triangle', 'noise', 'dpcm'];
  const visible = order.filter((c) => channels.includes(c));
  return (
    <div className="flex flex-wrap items-center gap-1">
      {visible.map((c) => (
        <ChannelBadge key={c} channel={c} />
      ))}
    </div>
  );
}

export function PatternLibrary({
  open,
  onClose,
  currentPatternRows,
  rowsPerPattern,
  cursorRow,
  onInsert,
}: Props) {
  const [mode, setMode] = useState<Mode>('browse');
  const [userPresets, setUserPresets] = useState<PatternPreset[]>([]);
  const [categoryFilter, setCategoryFilter] = useState<PresetCategory | 'all'>('all');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /** Per-preset chosen root note, keyed by `source:name`. Falls back to preset.rootNote. */
  const [chosenRoot, setChosenRoot] = useState<Record<string, RootLetter>>({});
  /** Per-preset explicit loop override, keyed by `source:name`. Falls back to isLoopable(preset). */
  const [loopOverride, setLoopOverride] = useState<Record<string, boolean>>({});

  // Save-mode state
  const [newName, setNewName] = useState('');
  const [newCategory, setNewCategory] = useState<PresetCategory>('drums');
  const [newDescription, setNewDescription] = useState('');
  const [newChannels, setNewChannels] = useState<Record<ChannelName, boolean>>({
    pulse1: false,
    pulse2: false,
    triangle: false,
    noise: true,
    dpcm: true,
  });

  useEffect(() => {
    if (!open) return;
    setMode('browse');
    setError(null);
    setLoading(true);
    listPresets()
      .then((list) => setUserPresets(list))
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  }, [open]);

  const allPresets = useMemo(
    () => [...BUILTIN_PRESETS, ...userPresets],
    [userPresets],
  );
  const filtered = useMemo(
    () =>
      categoryFilter === 'all'
        ? allPresets
        : allPresets.filter((p) => p.category === categoryFilter),
    [allPresets, categoryFilter],
  );

  async function refreshUser() {
    setLoading(true);
    try {
      setUserPresets(await listPresets());
    } finally {
      setLoading(false);
    }
  }

  async function handleDelete(name: string) {
    try {
      await deletePreset(name);
      await refreshUser();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  async function handleSavePreset() {
    setError(null);
    if (!newName.trim()) {
      setError('Name is required.');
      return;
    }
    const channels = (Object.keys(newChannels) as ChannelName[]).filter((c) => newChannels[c]);
    if (channels.length === 0) {
      setError('Pick at least one channel to capture.');
      return;
    }
    // Filter rows to keep only the selected channels.
    const rows: DemoRow[] = currentPatternRows.map((row) => {
      const out: DemoRow = {};
      for (const ch of channels) {
        const val = (row as Record<string, unknown>)[ch];
        if (val !== undefined) (out as Record<string, unknown>)[ch] = val;
      }
      return out;
    });
    try {
      await savePreset({
        name: newName,
        category: newCategory,
        channels,
        rows,
        rowsPerPattern,
        source: 'user',
        description: newDescription || undefined,
      });
      await refreshUser();
      setMode('browse');
      setNewName('');
      setNewDescription('');
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div
        className="flex max-h-[80vh] w-full max-w-2xl flex-col rounded border border-nes-cyan/40 bg-nes-dim p-4 shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="mb-3 flex items-center justify-between gap-3">
          <h3 className="text-sm font-bold text-nes-cyan">
            Pattern library {mode === 'save' ? '· save preset' : ''}
          </h3>
          <button
            onClick={onClose}
            className="rounded border border-nes-white/30 px-2 py-1 text-xs text-nes-white/70 hover:bg-nes-white/5"
          >
            Close
          </button>
        </header>

        {mode === 'browse' && (
          <>
            <div className="mb-3 flex flex-wrap items-center gap-2">
              <button
                onClick={() => setMode('save')}
                className="rounded bg-nes-cyan px-2 py-1 text-xs font-bold text-nes-black hover:bg-nes-cyan/80"
              >
                + Save current as preset…
              </button>
              <span className="ml-2 text-[10px] text-nes-white/40">
                Insert anchors at cursor row {cursorRow}.
              </span>
              <div className="ml-auto flex items-center gap-1 text-[10px]">
                <span className="text-nes-white/40">filter:</span>
                <select
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value as PresetCategory | 'all')}
                  className="rounded border border-nes-cyan/30 bg-nes-black px-2 py-[1px] text-nes-cyan"
                >
                  <option value="all">all</option>
                  {PRESET_CATEGORIES.map((c) => (
                    <option key={c} value={c}>
                      {CATEGORY_LABEL[c]}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {loading && <p className="text-xs text-nes-white/40">Loading…</p>}
            {error && <p className="mb-2 text-xs text-nes-red">{error}</p>}

            <ul className="flex flex-col gap-1 overflow-y-auto pr-1 text-xs">
              {filtered.map((p) => {
                const key = `${p.source}:${p.name}`;
                const root = chosenRoot[key] ?? p.rootNote ?? 'C';
                const semitones = p.rootNote ? rootDelta(p.rootNote, root) : 0;
                const loop = loopOverride[key] ?? isLoopable(p);
                return (
                  <li
                    key={key}
                    className={`flex flex-col gap-2 rounded border bg-nes-black px-2 py-2 ${
                      p.source === 'builtin' ? 'border-nes-cyan/20' : 'border-nes-white/20'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-bold text-nes-cyan">{p.name}</span>
                          <span
                            className={`rounded px-1 text-[10px] ${
                              p.source === 'builtin'
                                ? 'bg-nes-cyan/20 text-nes-cyan'
                                : 'bg-nes-white/10 text-nes-white/70'
                            }`}
                          >
                            {p.source}
                          </span>
                        </div>
                        <div className="mt-1 flex flex-wrap items-center gap-2">
                          <span className="text-[10px] uppercase tracking-wider text-nes-white/40">
                            fills:
                          </span>
                          <ChannelBadges channels={p.channels} />
                          <span className="text-[10px] text-nes-white/40">
                            · {CATEGORY_LABEL[p.category]} · {p.rowsPerPattern} rows
                            {p.rootNote ? ` · authored in ${p.rootNote}` : ''}
                          </span>
                        </div>
                        {p.description && (
                          <p className="mt-1 text-[10px] text-nes-white/50">{p.description}</p>
                        )}
                      </div>
                      <div className="flex shrink-0 flex-col items-end gap-1">
                        <button
                          onClick={() => onInsert(p, cursorRow, semitones, loop)}
                          className="rounded bg-nes-cyan px-2 py-[2px] text-[10px] font-bold text-nes-black hover:bg-nes-cyan/80"
                        >
                          Insert
                          {semitones !== 0 ? ` (${semitones > 0 ? '+' : ''}${semitones})` : ''}
                          {loop ? ' · loop' : ''}
                        </button>
                        <label
                          className="flex items-center gap-1 text-[10px] text-nes-white/50"
                          title="Tile the preset to fill from cursor to end of pattern"
                        >
                          <input
                            type="checkbox"
                            checked={loop}
                            onChange={(e) =>
                              setLoopOverride({ ...loopOverride, [key]: e.target.checked })
                            }
                            className="accent-nes-cyan"
                          />
                          loop to fill
                        </label>
                        {p.source === 'user' && (
                          <button
                            onClick={() => {
                              if (window.confirm(`Delete preset "${p.name}"?`))
                                void handleDelete(p.name);
                            }}
                            className="rounded border border-nes-red/40 px-2 py-[2px] text-[10px] text-nes-red hover:bg-nes-red/10"
                          >
                            ×
                          </button>
                        )}
                      </div>
                    </div>

                    {p.rootNote && (
                      <div className="flex flex-wrap items-center gap-1">
                        <span className="text-[10px] uppercase tracking-wider text-nes-white/40">
                          insert in root:
                        </span>
                        {ROOT_LETTERS.map((r) => (
                          <button
                            key={r}
                            onClick={() => setChosenRoot({ ...chosenRoot, [key]: r })}
                            className={`rounded px-[6px] py-[1px] text-[10px] font-bold transition ${
                              r === root
                                ? 'bg-nes-cyan text-nes-black'
                                : 'border border-nes-cyan/20 text-nes-cyan/70 hover:bg-nes-cyan/10'
                            }`}
                          >
                            {r}
                          </button>
                        ))}
                      </div>
                    )}
                  </li>
                );
              })}
              {filtered.length === 0 && (
                <li className="text-xs text-nes-white/40">No presets in this category yet.</li>
              )}
            </ul>
          </>
        )}

        {mode === 'save' && (
          <div className="flex flex-col gap-3 text-xs">
            <label className="flex flex-col gap-1">
              <span className="text-nes-white/70">Name</span>
              <input
                autoFocus
                type="text"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="e.g. my groove"
                className="rounded border border-nes-cyan/30 bg-nes-black px-2 py-1 text-nes-white"
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-nes-white/70">Category</span>
              <select
                value={newCategory}
                onChange={(e) => setNewCategory(e.target.value as PresetCategory)}
                className="rounded border border-nes-cyan/30 bg-nes-black px-2 py-1 text-nes-cyan"
              >
                {PRESET_CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {CATEGORY_LABEL[c]}
                  </option>
                ))}
              </select>
            </label>
            <fieldset>
              <legend className="mb-1 text-nes-white/70">
                Capture channels — these are the ones the new preset will overwrite when inserted
              </legend>
              <div className="flex flex-wrap gap-3">
                {(['pulse1', 'pulse2', 'triangle', 'noise', 'dpcm'] as ChannelName[]).map((ch) => (
                  <label
                    key={ch}
                    className="flex items-center gap-1.5 text-[11px] text-nes-white/80"
                  >
                    <input
                      type="checkbox"
                      checked={newChannels[ch]}
                      onChange={(e) => setNewChannels({ ...newChannels, [ch]: e.target.checked })}
                      className="accent-nes-cyan"
                    />
                    <ChannelBadge channel={ch} />
                  </label>
                ))}
              </div>
            </fieldset>
            <label className="flex flex-col gap-1">
              <span className="text-nes-white/70">Description (optional)</span>
              <input
                type="text"
                value={newDescription}
                onChange={(e) => setNewDescription(e.target.value)}
                className="rounded border border-nes-cyan/30 bg-nes-black px-2 py-1 text-nes-white"
              />
            </label>

            {error && <p className="text-nes-red">{error}</p>}

            <div className="mt-1 flex justify-end gap-2">
              <button
                onClick={() => setMode('browse')}
                className="rounded border border-nes-white/30 px-3 py-1 hover:bg-nes-white/5"
              >
                Cancel
              </button>
              <button
                onClick={handleSavePreset}
                className="rounded bg-nes-cyan px-3 py-1 font-bold text-nes-black hover:bg-nes-cyan/80"
              >
                Save preset
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
